import { useCallback, useMemo, useState } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { destinationInDisplayGroup } from "../../utils/censusDisplayGroups";
import type { DepartmentLocation, NurseInShift } from "../../utils/models";
import { type QuickAddPickSeed, shownJobIdFor } from "../../utils/scheduleConflict";
import type { SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { QuickAddStaffOption } from "./quickAddStaffOptions";

/** Every location name in the department once, A–Z. */
const locationOptionsFor = (locations: readonly DepartmentLocation[]): SystemAutoCompleteOption<string>[] =>
	Array.from(new Set(locations.map((loc) => loc.location)))
		.sort((a, b) => a.localeCompare(b))
		.map((loc) => ({ label: loc, value: loc }));

/**
 * The "+" dialog's person, job and location. Until the user picks a Location it is DERIVED, live, from the shift
 * of theirs the current times clash with (`locationFor`): that shift's own location when it lies inside the group
 * the "+" was clicked on — they are already in this group, not moving to a sibling it happens to list first — else
 * the group's first location; Unassigned leaves it empty and required rather than guessing one. Deriving it (not
 * setting it once at the pick) keeps it right when editing the times creates or changes that clash. A job or
 * location the user picked is never overridden.
 */
export const useQuickAddSelection = (sectionLocationKey: string) => {
	const { chosenDepartment } = useHospitalData();
	const { settings } = useUserSettingsContext();
	// The picked row, not just the person: a person working a double has a row per shift, and the picker shows which.
	const [staffOption, setStaffOption] = useState<QuickAddStaffOption | null>(null);
	const nurse = staffOption ? staffOption.value : null;
	const [jobId, setJobId] = useState<string | undefined>(undefined);
	const [jobTouched, setJobTouched] = useState(false);
	// `undefined` until the user picks a Location; `null` is their explicit "none".
	const [pickedLocation, setPickedLocation] = useState<string | null | undefined>(undefined);

	const nurseJobs = useMemo(() => nurse?.jobs ?? [], [nurse]);
	/** The job shown, offered lengths for and saved, given the shift being moved now, if any (`shownJobIdFor`). */
	const jobFor = useCallback(
		(movingShift: NurseInShift | undefined) => {
			const shownJobId = shownJobIdFor({ jobId, jobTouched, heldJobIds: nurseJobs.map((candidate) => candidate.id), movingShift });
			return { jobId: shownJobId, job: nurseJobs.find((candidate) => candidate.id === shownJobId) };
		},
		[jobId, jobTouched, nurseJobs],
	);
	/** The destination for the shift the current times clash with (`undefined` when none does). */
	const locationFor = useCallback(
		(blockingShift: NurseInShift | undefined): string | null =>
			pickedLocation !== undefined
				? pickedLocation
				: destinationInDisplayGroup(chosenDepartment.locations, settings.censusByLocation, sectionLocationKey, blockingShift?.location),
		[pickedLocation, chosenDepartment.locations, settings.censusByLocation, sectionLocationKey],
	);
	const locationOptions = useMemo(() => locationOptionsFor(chosenDepartment.locations), [chosenDepartment.locations]);

	/** Apply a pick of a staff row and the job it brings (`null` for a cleared pick). */
	const applyPick = useCallback((picked: QuickAddStaffOption | null, seed: QuickAddPickSeed | null) => {
		setStaffOption(picked);
		setJobTouched(false);
		setJobId(seed ? seed.jobId : undefined);
	}, []);

	const pickJob = useCallback((picked: string | undefined) => {
		setJobId(picked);
		setJobTouched(true);
	}, []);

	return { staffOption, nurse, nurseJobs, jobFor, jobTouched, locationFor, locationOptions, applyPick, pickJob, pickLocation: setPickedLocation };
};
